import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';
import { RRule } from 'rrule';
import { createNonMemoizedSelector } from 'store/selectors';
import { Tier } from 'features/premium/entities';
import { selectUserTier } from 'features/premium/selectors/tier';
import { selectSelectedSpace, selectSpaces } from 'features/spaces/reducers/selectors';
import { selectIsGBUser } from 'reducers/selectors/user';
import checkFeatureFlag from 'utils/featureFlags';
import { addFlashListSectionedDataToActivities, getActivitiesSectionedFlashlist, } from 'utils/formatting/activities';
import { assertUnreachable } from 'utils/types';
import * as debug from '../debug';
import { SavingsPlanRuleType, } from '../types';
import { getPotDisplayName } from '../utils';
export const selectPots = (state) => debug.useFakeData || state.pots.pots;
export const selectPotProductsWithInterest = createSelector((store) => store.pots.products, (products) => products?.filter((product) => product.isVisible &&
    (product.type === 'INTEREST_NOTICE' || product.type === 'INTEREST') &&
    product.rates.tierRates.length));
export const selectSpacePots = (state) => state.pots.spacePots;
export const selectCurrentSpacePots = createSelector([(store) => store.pots.spacePots, selectSelectedSpace], (spacePots, spaceId) => {
    if (spacePots && spaceId && spacePots[spaceId]) {
        return spacePots[spaceId];
    }
    return [];
});
export const selectPotsWithSpaceId = createSelector([selectPots, selectSpaces], (pots, spaces) => {
    const defaultSpaceId = spaces.find((space) => space.isDefault)?.id;
    return pots.map((pot) => ({
        ...pot,
        spaceId: pot.spaceId || defaultSpaceId,
    }));
});
export const selectUserCreatedPot = createSelector((state) => state.pots.pots, (pots) => {
    if (pots.length) {
        const pot = pots.find((pot) => !pot.type.startsWith('ACCUMULATED_INTEREST'));
        if (pot) {
            return pot;
        }
    }
    return undefined;
});
export const selectUserCreatedPots = createSelector((state) => state.pots.pots, (pots) => {
    if (pots.length) {
        return pots.filter((pot) => !pot.type.startsWith('ACCUMULATED_INTEREST'));
    }
    return [];
});
export const selectUserCreatedInterestPots = createSelector(selectUserCreatedPots, (pots) => pots.filter((pot) => pot.type !== 'BASIC'));
export const selectPotProductByIdMap = createSelector((store) => store.pots.products, (products) => (products ?? []).reduce((acc, product) => {
    acc[product.id] = product;
    return acc;
}, {}));
export const selectAddMoneyPotsSortedByBalance = createSelector([
    selectUserCreatedInterestPots,
    (store) => store.pots.interestPotProductsTierRateMap,
    selectUserTier,
    selectPotProductByIdMap,
], (pots, interestPotProductsTierRateMap, userTier, productsMap) => pots
    .map((pot) => ({
    ...pot,
    interestRate: interestPotProductsTierRateMap?.[pot.productId]?.[userTier],
    productName: productsMap?.[pot.productId]?.name,
}))
    .sort((a, b) => b.balance.value - a.balance.value || a.name.localeCompare(b.name)));
export const selectVisibleAutosaveDistribution = createSelector((state) => state.pots.savingsPlan?.distribution, selectUserCreatedInterestPots, (distribution, pots) => {
    if (!distribution) {
        return distribution;
    }
    const { WALLET: _wallet, ...distributionWithoutWallet } = distribution;
    if (!pots.length) {
        return distributionWithoutWallet;
    }
    const visiblePotIds = new Set(pots.map((pot) => pot.id));
    return Object.fromEntries(Object.entries(distributionWithoutWallet).filter(([potId]) => visiblePotIds.has(potId)));
});
export const selectSortedAutosavePots = createSelector([selectUserCreatedInterestPots, selectVisibleAutosaveDistribution], (pots, distribution) => {
    const alphabetical = [...pots].sort((a, b) => a.name.localeCompare(b.name));
    if (!distribution) {
        return alphabetical;
    }
    const withPositiveDistribution = [];
    const rest = [];
    alphabetical.forEach((pot) => {
        if ((distribution[pot.id] || 0) > 0) {
            withPositiveDistribution.push(pot);
        }
        else {
            rest.push(pot);
        }
    });
    withPositiveDistribution.sort((a, b) => (distribution[b.id] || 0) - (distribution[a.id] || 0));
    return [...withPositiveDistribution, ...rest];
});
export const selectPendingTopupsFlag = (state) => Boolean(debug.emulatePendingTopups || state.pots.pendingTopupsFlag);
export const selectCashWithdrawableBalance = (state) => state.pots.cashWithdrawable;
export const selectPotsStatuses = createSelector((state) => state.pots.account?.status, (potsAccountStatus) => ({
    status: potsAccountStatus,
}));
export const selectActivePotInterest = createSelector([
    (_state, potProductId) => potProductId,
    selectUserTier,
    (store) => store.pots.interestPotProductsTierRateMap,
], (potProductId, activeTier, interestPotProductsTierRateMap) => activeTier && potProductId ? interestPotProductsTierRateMap?.[potProductId]?.[activeTier] : undefined);
/**
 * Selects activeRate, tierRateMap and hasRateUpgrade
 *
 * on the basis of pot product
 *
 * or
 *
 * on the basis of highest interest rates
 */
export const selectInterestInfoForProductId = createSelector([
    (_state, potProductId) => potProductId,
    (store) => store.pots.interestPotProductsTierRateMap,
    (store) => store.pots.interestPotsTierRateMap,
    selectUserTier,
], (potProductId, interestPotProductsTierRateMap, maxInterestRatesMap, userTier) => {
    if (potProductId) {
        const tierRateMap = interestPotProductsTierRateMap?.[potProductId];
        const ultimateRate = tierRateMap?.[Tier.ultimate];
        const userRate = tierRateMap?.[userTier];
        return {
            tierRateMap,
            activeRate: userRate,
            hasRateUpgrade: ultimateRate && userRate ? Number(ultimateRate) > Number(userRate) : false,
        };
    }
    const ultimateRate = maxInterestRatesMap?.[Tier.ultimate];
    const userRate = maxInterestRatesMap?.[userTier];
    return {
        tierRateMap: maxInterestRatesMap,
        activeRate: maxInterestRatesMap?.[userTier],
        hasRateUpgrade: ultimateRate && userRate ? Number(ultimateRate) > Number(userRate) : false,
    };
});
const getPotTypeSortRank = (type) => {
    switch (type) {
        case 'INTEREST':
        case 'ACCUMULATED_INTEREST':
            return 0;
        case 'INTEREST_NOTICE':
        case 'ACCUMULATED_INTEREST_NOTICE':
            return 1;
        case 'BASIC':
            return 2;
        default:
            assertUnreachable(type);
            return 3;
    }
};
const getRecommendedSortRank = (product) => {
    if (!product?.isRecommended) {
        return 2;
    }
    return product.isProductCashISA ? 0 : 1;
};
export const selectPickerPots = createSelector([
    selectUserCreatedInterestPots,
    (store) => store.pots.interestPotProductsTierRateMap,
    selectUserTier,
    selectPotProductByIdMap,
], (pots, interestPotProductsTierRateMap, userTier, productsMap) => pots
    .map((pot) => ({
    ...pot,
    interestRate: interestPotProductsTierRateMap?.[pot.productId]?.[userTier],
    productName: productsMap?.[pot.productId]?.name,
}))
    .sort((a, b) => {
    const recommendedRankDiff = getRecommendedSortRank(productsMap[a.productId]) - getRecommendedSortRank(productsMap[b.productId]);
    if (recommendedRankDiff !== 0) {
        return recommendedRankDiff;
    }
    const rankDiff = getPotTypeSortRank(a.type) - getPotTypeSortRank(b.type);
    if (rankDiff !== 0) {
        return rankDiff;
    }
    return b.balance.value - a.balance.value || a.name.localeCompare(b.name);
}));
export const selectNonBasicPots = createSelector([(store) => store.pots.pots], (pots) => pots.filter((pot) => pot.type !== 'BASIC'));
export const selectPickerPotsWithAccInterestPots = createSelector([
    selectNonBasicPots,
    (store) => store.pots.interestPotProductsTierRateMap,
    selectUserTier,
    selectPotProductByIdMap,
], (pots, interestPotProductsTierRateMap, userTier, productsMap) => pots
    .map((pot) => ({
    ...pot,
    interestRate: interestPotProductsTierRateMap?.[pot.productId]?.[userTier],
    productName: productsMap?.[pot.productId]?.name,
}))
    .sort((a, b) => {
    const recommendedRankDiff = getRecommendedSortRank(productsMap[a.productId]) - getRecommendedSortRank(productsMap[b.productId]);
    if (recommendedRankDiff !== 0) {
        return recommendedRankDiff;
    }
    const rankDiff = getPotTypeSortRank(a.type) - getPotTypeSortRank(b.type);
    if (rankDiff !== 0) {
        return rankDiff;
    }
    return b.balance.value - a.balance.value || a.name.localeCompare(b.name);
}));
export const selectTransferPotsFromPotType = createSelector([
    (_state, type) => type,
    (_state, _type, productId) => productId,
    selectPickerPots,
], (type, productId, pots) => {
    if (type && productId) {
        if (type === 'BASIC' || type === 'INTEREST' || type === 'INTEREST_NOTICE') {
            return pots.filter((pot) => pot.type === type && pot.productId === productId);
        }
        if (type === 'ACCUMULATED_INTEREST') {
            return pots.filter((pot) => (pot.type === 'ACCUMULATED_INTEREST' || pot.type === 'INTEREST') && pot.productId === productId);
        }
        if (type === 'ACCUMULATED_INTEREST_NOTICE') {
            return pots.filter((pot) => (pot.type === 'ACCUMULATED_INTEREST_NOTICE' || pot.type === 'INTEREST_NOTICE') &&
                pot.productId === productId);
        }
        assertUnreachable(type);
    }
    return pots;
});
export const selectTransferPotsFromPotTypeWithAccInterestPots = createSelector([
    (_state, type) => type,
    (_state, _type, productId) => productId,
    selectPickerPotsWithAccInterestPots,
], (type, productId, pots) => {
    if (type && productId) {
        if (type === 'INTEREST' || type === 'ACCUMULATED_INTEREST') {
            return pots.filter((pot) => (pot.type === 'ACCUMULATED_INTEREST' || pot.type === 'INTEREST') && pot.productId === productId);
        }
        if (type === 'INTEREST_NOTICE' || type === 'ACCUMULATED_INTEREST_NOTICE') {
            return pots.filter((pot) => (pot.type === 'ACCUMULATED_INTEREST_NOTICE' || pot.type === 'INTEREST_NOTICE') &&
                pot.productId === productId);
        }
    }
    return pots;
});
/**
 * Selects length of pots of type BASIC
 */
export const selectHasMoreBasicPots = createSelector((store) => store.pots.pots, (pots) => pots.filter((pot) => pot.type === 'BASIC').length > 1);
/**
 * Selects length of pots of type INTEREST as well as ACCUMULATED_INTEREST
 */
export const selectHasMoreAllInterestPots = createSelector([(_state, productId) => productId, (store) => store.pots.pots], (productId, pots) => pots.filter((pot) => productId === pot.productId && (pot.type === 'INTEREST' || pot.type === 'ACCUMULATED_INTEREST')).length > 1);
/**
 * Selects length of pots of type INTEREST as well as ACCUMULATED_INTEREST
 */
export const selectHasMoreAllNoticeInterestPots = createSelector([(_state, productId) => productId, (store) => store.pots.pots], (productId, pots) => pots.filter((pot) => productId === pot.productId && (pot.type === 'INTEREST_NOTICE' || pot.type === 'ACCUMULATED_INTEREST_NOTICE')).length > 1);
/**
 * Selects length of pots of type INTEREST
 */
export const selectHasMoreInterestPots = createSelector([(_state, productId) => productId, (store) => store.pots.pots], (productId, pots) => pots.filter((pot) => pot.productId === productId && pot.type === 'INTEREST').length > 1);
/**
 * Selects length of pots of type INTEREST_NOTICE
 */
export const selectHasMoreNoticeInterestPots = createSelector([(_state, productId) => productId, (store) => store.pots.pots], (productId, pots) => pots.filter((pot) => pot.productId === productId && pot.type === 'INTEREST_NOTICE').length > 1);
/**
 * Selects the maximum pot rate which is in Tier,ultimate
 */
export const selectMaximumInterest = createSelector((store) => store.pots.interestPotsTierRateMap, (tierRateMap) => tierRateMap?.[Tier.ultimate]);
export const selectUserTierMaxPotInterest = createSelector((store) => store.pots.interestPotsTierRateMap, selectUserTier, (tierRateMap, userTier) => {
    if (userTier && tierRateMap?.[userTier]) {
        return tierRateMap[userTier];
    }
    return '';
});
export const selectHasInterestPots = createSelector((store) => store.pots.pots, (pots) => pots.find((pot) => pot.type === 'INTEREST' ||
    pot.type === 'ACCUMULATED_INTEREST' ||
    pot.type === 'INTEREST_NOTICE' ||
    pot.type === 'ACCUMULATED_INTEREST_NOTICE'));
export const selectHasPots = createSelector((store) => store.pots.pots, (pots) => !!pots.length);
export const selectShouldShowOpenPotsNotificationBanner = createSelector([
    (_state, type) => type,
    (state) => state.deviceSettings.didCloseOpenPotsCTAFromAnalytics,
    (state) => state.deviceSettings.didCloseOpenPotsCTAFromBudgeting,
    (state) => state.deviceSettings.didCloseOpenPotsCTAFromTransactions,
    (state) => state.deviceSettings.didCloseOpenPotsCTAFromPots,
], (type, didCloseOpenPotsCTAFromAnalytics, didCloseOpenPotsCTAFromBudgeting, didCloseOpenPotsCTAFromTransactions, didCloseOpenPotsCTAFromPots) => {
    switch (type) {
        case 'Analytics':
            return didCloseOpenPotsCTAFromAnalytics;
        case 'Budgeting':
            return didCloseOpenPotsCTAFromBudgeting;
        case 'Transactions':
            return didCloseOpenPotsCTAFromTransactions;
        case 'Pots':
            return didCloseOpenPotsCTAFromPots;
        default:
            return false;
    }
});
export const selectHasInterestPotProduct = createSelector((store) => store.pots.products, (products) => {
    const interestProduct = products?.find((product) => product.type === 'INTEREST' || product.type === 'INTEREST_NOTICE');
    return !!interestProduct;
});
export const selectPotsTotalBalance = createSelector([(store) => store.pots.spacePots, selectSelectedSpace, (store) => store.user.user.currency || 'GBP'], (spacePots, spaceId, defaultCurrency) => {
    const defaultValue = {
        value: 0,
        currency: defaultCurrency,
    };
    if (!spaceId)
        return defaultValue;
    const pots = spacePots?.[spaceId] || [];
    if (!pots.length)
        return defaultValue;
    return {
        value: pots.reduce((prev, curr) => prev + (curr.primaryCurrencyBalance?.value || curr.balance.value), 0),
        currency: pots[0].primaryCurrencyBalance?.currency || pots[0].balance.currency,
    };
});
export const selectPotProductFromProductId = createSelector([(_state, productId) => productId, (state) => state.pots.products], (productId, potProducts) => {
    if (productId) {
        return potProducts?.find((product) => product.id === productId);
    }
    return undefined;
});
export const selectPotFromPotId = createSelector([(_state, potId) => potId, (state) => state.pots.pots], (potId, pots) => {
    if (potId) {
        return pots.find((pot) => pot.id === potId);
    }
    return undefined;
});
export const selectInitialDepositActivity = createSelector([(state) => state.unpersistedPotsReducer.pendingInitialDeposit, (state) => state.pots.pots], (activity, pots) => {
    if (!activity)
        return undefined;
    const pot = pots.find((p) => p.id === activity.potId);
    return pot ? { activity, pot } : undefined;
});
export const selectIsPotsAccountActive = (state) => state.pots.account?.status === 'ACTIVE';
export const selectIsPotsAccountNotStarted = (state) => state.pots.account?.status === 'NOT_STARTED';
export const selectPotProductWithTheHighestInterest = createSelector([(store) => store.pots.productIdWithTheHighestInterest, (store) => store.pots.products], (productIdWithTheHighestInterest, potProducts) => {
    if (productIdWithTheHighestInterest) {
        return potProducts?.find((product) => product.id === productIdWithTheHighestInterest);
    }
    return undefined;
});
/**
 * this selector forcefully shows pots product tour
 *
 * this is for legacy users who had their pots account turned to !NOT_STARTED
 * due to sideeffect from invest account opening
 */
export const selectShouldForceShowPotsOnb = createSelector((store) => store.deviceSettings.hasTriedSettingsUpPots, (state) => state.pots.account?.status, (hasTriedSettingsUpPots, potsAccStatus) => !hasTriedSettingsUpPots && potsAccStatus !== 'NOT_STARTED' && potsAccStatus !== 'ACTIVE');
export const selectLatestUpcomingPayment = createSelector((store) => store.unpersistedPotsReducer.upcomingAutosavePayments, (upcomingAutosavePayments) => {
    if (debug.showFakeUpcomingAutosavePayments) {
        return debug.fakeUpcomingAutosavePayments?.[0];
    }
    if (upcomingAutosavePayments?.[0]) {
        return upcomingAutosavePayments[0];
    }
    return undefined;
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectLatestAutosaveActivity = createSelector((store) => store.unpersistedPotsReducer.autoSaveActivities, (autoSaveActivities) => {
    if (debug.showFakeAutosaveActivities) {
        return debug.fakeAutosaveActivities?.[0];
    }
    if (autoSaveActivities?.[0]) {
        return autoSaveActivities[0];
    }
    return undefined;
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectAutosaveActivityToDisplayInAutosave = createNonMemoizedSelector(selectLatestUpcomingPayment, selectLatestAutosaveActivity, (upcomingAutosavePayments, autoSaveActivities) => {
    if (upcomingAutosavePayments) {
        return upcomingAutosavePayments;
    }
    if (autoSaveActivities) {
        return autoSaveActivities;
    }
    return undefined;
});
export const selectAutosaveActivitySections = createSelector(selectLatestUpcomingPayment, (state) => debug.showFakeAutosaveActivities ? debug.fakeAutosaveActivities : state.unpersistedPotsReducer.autoSaveActivities, (latestUpcomingPayment, activities) => {
    const pendingActivities = [];
    const nonPendingActivities = [];
    activities?.forEach((activity) => {
        if (activity.status === 'PENDING') {
            pendingActivities.push(activity);
        }
        else {
            nonPendingActivities.push(activity);
        }
    });
    const activitySection = nonPendingActivities?.length
        ? getActivitiesSectionedFlashlist(nonPendingActivities, 'timestamp')
        : [];
    const upcomingSection = latestUpcomingPayment
        ? [
            {
                id: 'Upcoming',
                sectionTitle: 'Upcoming',
            },
            latestUpcomingPayment,
        ]
        : [];
    const pendingSection = pendingActivities.length
        ? [
            {
                id: 'Pending',
                sectionTitle: 'Pending',
            },
            ...addFlashListSectionedDataToActivities(pendingActivities),
        ]
        : [];
    return [...upcomingSection, ...pendingSection, ...activitySection];
});
export const selectAutosaveActivitiesInitialLoading = createSelector((state) => state.unpersistedPotsReducer.fetchingAutosaveActivities, (state) => state.unpersistedPotsReducer.autosaveActivitiesPaginationToken, (requesting, paginationToken) => !!(requesting && paginationToken === undefined));
export const selectPotIdPotInfoMap = createSelector(selectPots, (pots) => pots.reduce((accumulator, currentValue) => ({ ...accumulator, [currentValue.id]: currentValue }), {}));
export const selectShouldShowAutosave = createSelector((store) => checkFeatureFlag(store.utils.featureFlags, 'automated_savings_v2'), (store) => store.pots.savingsPlan, (state) => state.user.user.guessedHomeCountry === 'GB', (hasAutosaveFlag, savingsPlan, isGBUser) => isGBUser && !!(savingsPlan?.mandateStatus || hasAutosaveFlag));
export const isSortCodeSame = (sortCode1, sortCode2) => {
    if (sortCode1 && sortCode2) {
        return sortCode1.replace(/[^a-zA-Z0-9]/g, '') === sortCode2.replace(/[^a-zA-Z0-9]/g, '');
    }
    return false;
};
export const selectAutosaveRulesArr = createSelector((store) => store.pots.savingsPlan, (savingsPlan) => {
    const rules = [];
    const { customAmount, automated, roundUpMultiplier } = savingsPlan || {};
    if (automated) {
        rules.push({
            type: SavingsPlanRuleType.AUTOMATED,
        });
    }
    if (customAmount) {
        rules.push({
            amount: customAmount,
            type: SavingsPlanRuleType.CUSTOM,
        });
    }
    if (roundUpMultiplier) {
        rules.push({
            roundUpMultiplier,
            type: SavingsPlanRuleType.ROUND_UP,
        });
    }
    return rules;
});
export const selectAutosaveFees = createSelector((store) => store.pots.savingsPlanTierFees, selectUserTier, (savingsPlanTierFees, tier) => savingsPlanTierFees?.[tier]);
// Pots on a product that is no longer offered aren't in the products list, so their
// section is named after the kind of pot rather than showing the product ID
const getFallbackSectionTitle = (type) => {
    switch (type) {
        case 'INTEREST':
        case 'ACCUMULATED_INTEREST':
            return 'Interest Pots';
        case 'INTEREST_NOTICE':
        case 'ACCUMULATED_INTEREST_NOTICE':
            return 'Notice Pots';
        default:
            return 'Instant Access Pots';
    }
};
const addToSection = (key, prev, curr, sectionTitle) => {
    if (prev[key]) {
        return {
            ...prev,
            [key]: {
                ...prev[key],
                arr: [...prev[key].arr, curr],
            },
        };
    }
    return {
        ...prev,
        [key]: {
            arr: [curr],
            sectionTitle,
        },
    };
};
export const selectPotFlashListData = createSelector([
    (state) => state.pots.pots,
    selectPotProductByIdMap,
    selectUserTier,
    (store) => store.pots.interestPotProductsTierRateMap,
], (pots, productByIdMap, activeTier, interestPotProductsTierRateMap) => {
    const finalArr = [];
    if (pots?.length) {
        const getInterestRateForSort = (productId) => {
            const rate = interestPotProductsTierRateMap?.[productId]?.[activeTier];
            return rate !== undefined ? Number(rate) : -Infinity;
        };
        const sortedPots = [...pots].sort((a, b) => {
            const recommendedRankDiff = getRecommendedSortRank(productByIdMap[a.productId]) - getRecommendedSortRank(productByIdMap[b.productId]);
            if (recommendedRankDiff !== 0) {
                return recommendedRankDiff;
            }
            const rankDiff = getPotTypeSortRank(a.type) - getPotTypeSortRank(b.type);
            if (rankDiff !== 0) {
                return rankDiff;
            }
            if (a.productId !== b.productId) {
                const rateDiff = getInterestRateForSort(b.productId) - getInterestRateForSort(a.productId);
                if (rateDiff !== 0) {
                    return rateDiff;
                }
            }
            const balanceDiff = b.balance.value - a.balance.value;
            if (balanceDiff !== 0) {
                return balanceDiff;
            }
            const accumulatedDiff = Number(a.type.startsWith('ACCUMULATED_INTEREST')) - Number(b.type.startsWith('ACCUMULATED_INTEREST'));
            if (accumulatedDiff !== 0) {
                return accumulatedDiff;
            }
            return a.name.localeCompare(b.name);
        });
        const potsMap = sortedPots.reduce((prev, curr) => {
            const productName = productByIdMap[curr.productId]?.name;
            const sectionTitle = productName ? `${productName}s` : getFallbackSectionTitle(curr.type);
            return addToSection(curr.productId, prev, curr, sectionTitle);
        }, {});
        Object.entries(potsMap).forEach(([productId, potSection]) => {
            const arrLength = potSection.arr.length;
            if (arrLength) {
                const totalBalance = potSection.arr.reduce((sum, pot) => sum + (pot.primaryCurrencyBalance?.value ?? pot.balance.value), 0);
                finalArr.push({
                    id: productId,
                    sectionTitle: potSection.sectionTitle,
                    totalBalance,
                    interestRate: activeTier ? interestPotProductsTierRateMap?.[productId]?.[activeTier] : undefined,
                });
                finalArr.push(...potSection.arr.map((pot, index) => ({
                    ...pot,
                    isFirstInSection: index === 0,
                    isLastInSection: index === arrLength - 1,
                })));
            }
        });
    }
    return finalArr;
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
const emptyPotActivities = [];
export const selectFirstFivePendingActivitiesForPot = createSelector([(_state, potId) => potId, (store) => store.unpersistedPotsReducer.potPendingActivities], (potId, potsActivities) => {
    if (potsActivities) {
        const data = potsActivities[potId];
        const dataLen = data?.length;
        if (dataLen) {
            return {
                slicedData: data.slice(0, 5),
                shouldShowSeeAll: dataLen > 5,
            };
        }
    }
    return {
        slicedData: emptyPotActivities,
        shouldShowSeeAll: false,
    };
});
export const selectFirstFiveActivitiesForPot = createSelector([(_state, potId) => potId, (store) => store.unpersistedPotsReducer.potActivities], (potId, potsActivities) => {
    if (potsActivities) {
        const data = potsActivities[potId];
        const dataLen = data?.length;
        if (dataLen) {
            return {
                slicedData: data.slice(0, 5),
                shouldShowSeeAll: dataLen > 5,
            };
        }
    }
    return {
        slicedData: emptyPotActivities,
        shouldShowSeeAll: false,
    };
});
export const selectInitialActivitiesLoadingForPot = createSelector([
    (_state, potId) => potId,
    (store) => store.unpersistedPotsReducer.requestingPotActivities,
    (store) => store.unpersistedPotsReducer.potActivitiesPaginationToken,
], (potId, requestingPotActivitiesObj, paginationTokenObj) => !!(requestingPotActivitiesObj?.[potId] && paginationTokenObj?.[potId] === undefined));
export const selectAutosaveCollectionFrequency = createSelector([(store) => store.pots.savingsPlan?.collectionFrequency], (collectionFrequency) => {
    if (collectionFrequency) {
        const { rrule } = collectionFrequency;
        const parsed = RRule.parseString(rrule);
        if (parsed.freq === RRule.MONTHLY) {
            return 'MONTHLY';
        }
        return 'WEEKLY';
    }
    return undefined;
});
export const selectAutosaveCollectionMonthlyRRule = createSelector([(store) => store.pots.savingsPlan?.collectionFrequency], (collectionFrequency) => {
    if (collectionFrequency) {
        const { rrule } = collectionFrequency;
        const parsed = RRule.parseString(rrule);
        if (typeof parsed.bymonthday === 'number') {
            switch (parsed.bymonthday) {
                case 1:
                    return { type: 'FIRST_WORKING_DAY', day: parsed.bymonthday };
                case -1:
                    return { type: 'LAST_WORKING_DAY' };
                default:
                    return { type: 'FIXED_MONTHLY_DATE', day: parsed.bymonthday };
            }
        }
    }
    return undefined;
});
export const selectShouldShowAutosaveSelectPeriod = createSelector((store) => store.utils.featureFlags, (featureFlags) => checkFeatureFlag(featureFlags, 'autosave_select_period'));
export const selectShouldShowPotsList = createSelector([
    (state) => state.pots.account?.status,
    (state) => state.pots.hasCompletedOnboardingFunnel,
    (state) => state.pots.savingsPlan,
    (state) => state.pots.autosaveLastPaymentCollectionDate,
], (status, hasCompletedOnboardingFunnel, savingsPlan, autosaveLastPaymentCollectionDate) => !!(status === 'ACTIVE' && (hasCompletedOnboardingFunnel || autosaveLastPaymentCollectionDate || !!savingsPlan)));
export const selectVisiblePotProducts = createSelector([(store) => store.pots.products], (products) => products?.filter((product) => product.isVisible));
export const selectAtMostThreeVisibleInterestPotProducts = createSelector([(store) => store.pots.products], (products) => products
    ?.filter((product) => product.isVisible && (product.type === 'INTEREST' || product.type === 'INTEREST_NOTICE'))
    .slice(0, 3));
export const selectFreeAutosaveOnPlus = createSelector((store) => store.utils.featureFlags, (featureFlags) => checkFeatureFlag(featureFlags, 'free_autosave_plus'));
export const selectUnopenedPotsInfo = createSelector([selectIsGBUser, (store) => store.pots.pots, (store) => store.pots.products], (isGBUser, pots, products) => {
    if (isGBUser && products) {
        const openedProductIds = new Set();
        for (const pot of pots) {
            if (pot.type === 'INTEREST' || pot.type === 'INTEREST_NOTICE') {
                return [];
            }
            openedProductIds.add(pot.productId);
        }
        const visibleProducts = products.filter((product) => product.isVisible);
        const unopenedPots = [];
        visibleProducts.forEach((product) => {
            if (!openedProductIds.has(product.id) && product.type !== 'BASIC') {
                unopenedPots.push({
                    id: product.id,
                    type: product.type,
                    productId: product.id,
                    productName: product.name,
                });
            }
        });
        const firstUnopenedPot = unopenedPots.shift();
        if (firstUnopenedPot) {
            unopenedPots.unshift({
                ...firstUnopenedPot,
                isFirst: true,
            });
        }
        const lastUnopenedPot = unopenedPots.pop();
        if (lastUnopenedPot) {
            unopenedPots.push({
                ...lastUnopenedPot,
                isLast: true,
            });
        }
        return unopenedPots;
    }
    return [];
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectHasCashIsaPot = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'cash_isa_pot'));
export const selectPotToDisplayInOnboardingFunnel = createSelector([selectUserCreatedPot], (pot) => pot
    ? {
        ...pot,
        name: getPotDisplayName(pot.name),
    }
    : undefined);
